import { ChevronRight, Copy, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router";
import { useToast } from "../../../components/ui/Toast";
import { CustomUrlStatusPill } from "./CustomUrlStatusPill";
import { useActiveTenant } from "../../../auth/AuthProvider";
import { formatLongDate, relativeFromNow } from "../date-utils";

export interface CustomUrlRowData {
  id: string;
  slug: string;
  targetType: "landing_page" | "internal_path";
  targetLandingPageId: string | null;
  targetPath: string | null;
  isActive: boolean;
  updatedAt: string;
  createdAt: string;
  /**
   * Optional field populated by the listing query for v1 if the API
   * returns it. If null, we just show the target type label.
   */
  targetLandingPageName?: string | null;
}

export interface CustomUrlStats {
  clicks: number;
  conversions: number;
}

/**
 * One row in the custom URLs listing. Shows slug, target type/value,
 * status pill, updated date, and a copy-URL button.
 */
export function CustomUrlRow({
  url,
  stats,
  statsLoading,
}: {
  url: CustomUrlRowData;
  stats?: CustomUrlStats;
  statsLoading?: boolean;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const tenantDomain = useActiveTenant().domain;

  const longDate = formatLongDate(url.updatedAt);
  const relative = relativeFromNow(url.updatedAt) ?? "just now";

  const copyUrl = async () => {
    const fullUrl = `https://${tenantDomain}/${url.slug}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.push({
        kind: "success",
        title: "Copied",
        description: `${fullUrl} copied to clipboard`,
      });
    } catch {
      toast.push({
        kind: "error",
        title: "Copy failed",
        description: "Could not copy to clipboard",
      });
    }
  };

  return (
    <button
      type="button"
      onClick={() => navigate(`/website/custom-urls/${url.id}`)}
      className="group relative flex w-full cursor-pointer items-center gap-3 px-5 py-4 text-left transition-colors duration-150 hover:bg-paper-2 active:bg-canvas/60 focus-visible:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tide/40"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-2 left-0 w-[3px] origin-top -translate-x-1 scale-y-50 rounded-r bg-tide opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:scale-y-100 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:scale-y-100 group-focus-visible:opacity-100"
      />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <CustomUrlStatusPill isActive={url.isActive} />
        </div>
        <div className="font-mono truncate text-[13px] text-ink-2 transition-colors duration-150 group-hover:text-tide group-focus-visible:text-tide">
          /{url.slug}
        </div>
        <div className="mt-0.5 truncate text-[12px] text-ink-4">
          {formatTarget(url)}
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-6 text-right text-[11px] text-ink-4">
        <div className="flex flex-col items-end">
          <div className="text-[9px] uppercase tracking-wider text-ink-5">Clicks</div>
          <div className="num font-medium">
            {statsLoading ? (
              <span className="pulse-soft inline-block h-3 w-8 rounded bg-canvas-2" />
            ) : stats ? (
              stats.clicks
            ) : (
              "—"
            )}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="text-[9px] uppercase tracking-wider text-ink-5">Conversions</div>
          <div className="num font-medium">
            {statsLoading ? (
              <span className="pulse-soft inline-block h-3 w-8 rounded bg-canvas-2" />
            ) : stats ? (
              stats.conversions
            ) : (
              "—"
            )}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="num">{longDate}</div>
          <div className="mt-0.5">{relative}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          copyUrl();
        }}
        aria-label="Copy URL"
        className="hidden lg:flex h-8 w-8 items-center justify-center rounded-md border border-ink/10 text-ink-4 transition-colors hover:border-tide hover:text-tide active:bg-canvas/60"
      >
        <Copy size={14} strokeWidth={2} />
      </button>
      <a
        href={`https://${tenantDomain}/${url.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label="Visit URL"
        className="hidden lg:flex h-8 w-8 items-center justify-center rounded-md border border-ink/10 text-ink-4 transition-colors hover:border-tide hover:text-tide active:bg-canvas/60"
      >
        <ExternalLink size={14} strokeWidth={2} />
      </a>
      <ChevronRight
        size={16}
        strokeWidth={1.8}
        aria-hidden
        className="flex-shrink-0 -translate-x-1 text-ink-5 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:text-tide group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
      />
    </button>
  );
}

function formatTarget(url: CustomUrlRowData): string {
  if (url.targetType === "landing_page") {
    if (url.targetLandingPageName) {
      return `Landing page: "${url.targetLandingPageName}"`;
    }
    return "Landing page";
  }
  // targetType === "internal_path"
  if (url.targetPath) {
    return url.targetPath;
  }
  return "Internal path";
}

